import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../../../state/ClientSelectionState/ClientSelection.state';
import { CompaniesService } from '../../../../../services/companies.service';
import { GeneralLedgerService } from '../../../../../services/general-ledger.service';
import {
  GeneralLedgerExportTypes,
  GLCategories,
  GLConfigCategory,
  GLConfiguration,
  GlDivision,
  GlMappingGroup,
  GLMappingLevelEnum,
  GlMappingRow,
  GlQboAccount
} from '../../../../../models/general-ledger';
import { Division } from '../../../../../models/division';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { CheckboxSizes } from '../../../../../components/checkbox/checkbox.component';
import { GeneralLedgerState } from '../../../../../state/GeneralLedgerState/GeneralLedger.state';
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'lib-gl-mapping',
  templateUrl: './gl-mapping.component.html',
  styleUrls: ['./gl-mapping.component.css'],
  standalone: false
})
export class GlMappingComponent implements OnInit {
  @Output()
  public cancelClicked = new EventEmitter<any>();
  @Input()
  public config: GLConfiguration;

  @Select(GeneralLedgerState.getBalance)
  public balance: Observable<number | null>;

  @Select(GeneralLedgerState.getQboAccounts)
  public qboAccounts: Observable<GlQboAccount[]>;

  @Select(GeneralLedgerState.getQboClasses)
  public qboClasses: Observable<GlQboAccount[]>;

  public existingMappingGroups: GlMappingGroup[] = [];
  public configCategories: GLConfigCategory[] = [];

  public columns: { name: string; key: string }[] = [];
  public currentAccountCols = 1;

  public divisions: GlDivision[] = [];
  public departments: GlDivision[] = [];
  public divDept: GlDivision[] = [];
  public jobCodes: GlDivision[] = [];

  public form: FormGroup;
  public exportByTypes: { val: number; name: string }[] = [
    { val: GLMappingLevelEnum.COMPANY, name: 'Company' }
  ];

  private clientCode: string;

  public INITIAL_CATEGORIES = [
    'EARNINGS',
    'DEDUCTIONS',
    'EMPLOYER_TAXES',
    'DISBURSEMENTS',
    'PAYMENTS',
    'EMPLOYER_CONTRIBUTIONS'
  ];

  public get sections() {
    return this.form.get('sections') as FormArray;
  }

  constructor(
    private store: Store,
    private glService: GeneralLedgerService,
    private companiesService: CompaniesService,
    private fb: FormBuilder
  ) {}
  ngOnInit() {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.clientCode = code;

      this.glService.getGlMappingGroups(code, this.config.Id).subscribe(groups => {
        this.existingMappingGroups = groups;

        this.glService.getGLConfigCategories(code).subscribe(cats => {
          const formSections: any[] = [];
          const finalArray: GLConfigCategory[] = [];
          cats.forEach(cat => {
            if (cat != null) {
              finalArray.push(cat);
            }
          });
          this.configCategories = finalArray;
          //Have to do this as a separate loop because in some cases, _initCategory needs to access the other categories.
          this.configCategories.forEach((cat, index) => {
            cat.Position = index;
            formSections.push(this._initCategory(cat));
          });

          this.form = this.fb.group({
            showNotExported: this.fb.control(false),
            sections: this.fb.array(formSections)
          });
        });
        this.glService.getGlDivisions(code).subscribe(glDivisionList => {
          const divisions = [];
          const departments = [];
          const divDepts = [];
          const jobCodes = [];

          glDivisionList.forEach(glDiv => {
            switch (glDiv.Parent) {
              case 'department':
                departments.push(glDiv);
                break;
              case 'division':
                divisions.push(glDiv);
                break;
              case 'division_department':
                divDepts.push(glDiv);
                break;
              case 'job code':
                jobCodes.push(glDiv);
                break;
            }
          });
          this.divisions = divisions;
          this.departments = departments;
          this.divDept = divDepts;
          this.jobCodes = jobCodes;

          if (this.jobCodes.length > 0) {
            this.exportByTypes.push({ val: GLMappingLevelEnum.JOB_CODE, name: 'Job Code' });
          }
          if (this.departments.length > 0) {
            this.exportByTypes.push({ val: GLMappingLevelEnum.DEPARTMENT, name: 'Department' });
          }
          if (this.divisions.length > 0) {
            this.exportByTypes.push({ val: GLMappingLevelEnum.DIVISION, name: 'Division' });
          }
          if (this.divDept.length > 0) {
            this.exportByTypes.push({
              val: GLMappingLevelEnum.DIVISION_AND_DEPARTMENT,
              name: 'Division & Department'
            });
          }
          this.exportByTypes.push({ val: GLMappingLevelEnum.NOT_EXPORTED, name: 'Not Exported' });
        });
      });
    });
  }

  public getArray(control: AbstractControl, name: string) {
    return control.get(name) as FormArray;
  }

  public cancel() {
    this.cancelClicked.emit(null);
  }

  public submit() {
    const formVal = this.form.value;
    const groups: GlMappingGroup[] = [];
    formVal.sections.forEach(section => {
      const category = this.configCategories.find(x => x.Display == section.name);
      const group: GlMappingGroup = {
        Level: section.exportBy,
        Category: this._getCategory(category.Code),
        ConfigurationId: this.config.Id,
        Id: null,
        Rows: []
      };
      const existing = this.existingMappingGroups.find(x => x.Category == group.Category);
      if (existing) {
        group.Id = existing.Id;
      }
      const cols = section.cols;
      section.rows.forEach(row => {
        row.accounts.forEach(account => {
          let divisionId = null;
          let departmentId = null;
          let jobCode = null;
          if (section.exportBy == GLMappingLevelEnum.DIVISION) {
            divisionId = parseInt(account.key);
          }
          if (section.exportBy == GLMappingLevelEnum.DEPARTMENT) {
            departmentId = parseInt(account.key);
          }
          if (section.exportBy == GLMappingLevelEnum.DIVISION_AND_DEPARTMENT) {
            const key = account.key as string;
            const split = key.split('&');
            divisionId = parseInt(split[0]);
            departmentId = parseInt(split[1]);
          }
          if (section.exportBy == GLMappingLevelEnum.JOB_CODE) {
            jobCode = parseInt(account.key);
          }
          const col = cols.find(x => x.key === account.key);

          const groupRow: GlMappingRow = {
            CheckName: null,
            CheckNumber: null,
            DivisionNumber: divisionId,
            DepartmentNumber: departmentId,
            GlAccount: account.value,
            GlClass: col.qboClass,
            IsDebit: row.transactionType == 'Debit',
            GroupId: existing ? existing.Id : null,
            Id: null,
            IsOffset: row.name.toLowerCase().includes('offset'),
            NotExported: row.notExported || section.exportBy == GLMappingLevelEnum.NOT_EXPORTED,
            TypeName: row.name,
            JobCode: jobCode,
            IsDefault: false
          };
          group.Rows.push(groupRow);
        });
      });
      groups.push(group);
    });

    if (this.existingMappingGroups.length > 0) {
      const updateCalls: any[] = [];
      for (let i = 0; i < groups.length; i++) {
        const element = groups[i];
        updateCalls.push(
          this.glService.updateGlMappingGroup(this.clientCode, this.config.Id, element.Id, element)
        );
      }
      forkJoin(updateCalls).subscribe(() => {
        this.cancelClicked.emit(null);
      });
    } else {
      this.glService
        .createGlMappingGroups(this.clientCode, this.config.Id, groups)
        .subscribe(() => {
          this.cancelClicked.emit(null);
        });
    }
  }
  public getColspan(code: string) {
    const div = this.divDept.find(x => x.Code == code);
    return div.DivisionChildren.length;
  }

  private _initCategory(parent: GLConfigCategory) {
    const array = [];
    const isInitialCategory = this.INITIAL_CATEGORIES.some(x => x === parent.Code);
    let initialMappingType = isInitialCategory
      ? GLMappingLevelEnum.COMPANY
      : GLMappingLevelEnum.NOT_EXPORTED;
    const existingGroup = this.existingMappingGroups.find(
      x => x.Category == this._getCategory(parent.Code)
    );
    if (existingGroup) {
      initialMappingType = existingGroup.Level;
    }
    const cols = this._getColumnDefinitions(initialMappingType);
    parent.Children.forEach(row => {
      // For Earnings, only the GROSS is shown by default.
      if (parent.Code === 'EARNINGS' && row.Code === 'GROSS') {
        array.push(this._initRow(row, cols, false, false, existingGroup?.Id));
      } else if (parent.Code === 'EARNINGS') {
        array.push(this._initRow(row, cols, true, null, existingGroup?.Id));
      } else if (parent.Code == 'DEDUCTIONS') {
        const disbursements = this.configCategories.find(x => x.Code == 'DISBURSEMENTS');
        if (
          disbursements.Children.some(x => x.Code == row.Code) ||
          row.Code == 'Total Deductions'
        ) {
          array.push(this._initRow(row, cols, true, null, existingGroup?.Id));
        } else {
          array.push(this._initRow(row, cols, false, true, existingGroup?.Id));
        }
      } else {
        let isCredit = false;
        if (parent.Code == 'DISBURSEMENTS' || parent.Code == 'PAYMENTS') {
          isCredit = true;
        }

        if (row.Code.startsWith('Total ') && isInitialCategory) {
          array.push(this._initRow(row, cols, false, isCredit, existingGroup?.Id));
        } else {
          array.push(this._initRow(row, cols, true, null, existingGroup?.Id));
        }
      }
    });
    const colsArray = this._createColumnGroup(cols, existingGroup);

    const group = this.fb.group({
      exportBy: this.fb.control(initialMappingType),
      rows: this.fb.array(array),
      name: this.fb.control(parent.Display),
      cols: this.fb.array(colsArray),
      topCols: this.fb.control(cols.topCols),
      id: existingGroup ? existingGroup.Id : null
    });

    group.get('exportBy').valueChanges.subscribe(val => {
      const newCols = this._getColumnDefinitions(val);
      const colGroup = this._createColumnGroup(newCols);
      const colsArray = group.get('cols') as FormArray;
      colsArray.clear();
      colGroup.forEach(col => colsArray.push(col));
      group.get('topCols').setValue(newCols.topCols);
      const rowControl = group.get('rows') as FormArray;
      const existing: any[] = rowControl.value;
      rowControl.clear();
      parent.Children.forEach(row => {
        const newRowControl = this._initRow(row, newCols);
        const existingRowValue = existing.find(x => x.name === newRowControl.get('name').value);
        if (existingRowValue != null) {
          newRowControl
            .get('notExported')
            .setValue(existingRowValue.notExported, { emitEvent: false });
          newRowControl
            .get('transactionType')
            .setValue(existingRowValue.transactionType, { emitEvent: false });
        }
        rowControl.push(newRowControl);
      });

      //recalculate column count
      const sections = this.form.get('sections').value.map(x => x.cols.length);
      this.currentAccountCols = Math.max(...sections);
    });
    return group;
  }

  private _createColumnGroup(cols: ColumnGroup, existingGroup: GlMappingGroup | null = null) {
    const colsArray: FormGroup[] = [];
    cols.cols.forEach(col => {
      let initialValue = null;
      if (existingGroup != null) {
        //we have an existing group.
        switch (existingGroup.Level) {
          case GLMappingLevelEnum.DIVISION:
            const existingDivCol = existingGroup.Rows.find(
              x => x.DivisionNumber.toString() == col.key
            );
            if (existingDivCol != null) initialValue = existingDivCol.GlClass;
            break;
          case GLMappingLevelEnum.DEPARTMENT:
            const existingDeptCol = existingGroup.Rows.find(
              x => x.DepartmentNumber.toString() == col.key
            );
            if (existingDeptCol != null) initialValue = existingDeptCol.GlClass;
            break;
          case GLMappingLevelEnum.DIVISION_AND_DEPARTMENT:
            const key = col.key as string;
            const split = key.split('&');
            const divisionId = parseInt(split[0]);
            const departmentId = parseInt(split[1]);
            const existingDivDeptCol = existingGroup.Rows.find(
              x => x.DepartmentNumber == departmentId && x.DivisionNumber == divisionId
            );
            if (existingDivDeptCol != null) initialValue = existingDivDeptCol.GlClass;
            break;
          case GLMappingLevelEnum.JOB_CODE:
            const existingJobCodeCol = existingGroup.Rows.find(
              x => x.JobCode.toString() == col.key
            );
            if (existingJobCodeCol != null) initialValue = existingJobCodeCol.GlClass;
            break;
          case GLMappingLevelEnum.COMPANY:
            initialValue = existingGroup.Rows[0].GlClass;
            break;
        }
      }
      colsArray.push(
        this.fb.group({
          key: col.key,
          name: col.name,
          qboClass: this.fb.control(initialValue)
        })
      );
    });
    return colsArray;
  }

  private _getCategory(name: string): GLCategories {
    return GLCategories[name as keyof typeof GLCategories];
  }
  private _getEnumFromCategory(cat: number): string {
    return GLCategories[cat];
  }
  private _initRow(
    row: GLConfigCategory,
    cols: ColumnGroup,
    notExported = false,
    isCredit: boolean | null = null,
    categoryId: number | null = null
  ) {
    let existing: GlMappingRow[] = this.existingMappingGroups
      .filter(x => {
        return x.Id == categoryId;
      })
      .flatMap(e => e.Rows)
      .filter(mapping => mapping.TypeName == row.Code);
    if (existing.length > 0) {
      const first = existing[0];
      isCredit = !first.IsDebit;
      notExported = first.NotExported;
    }
    const accountControls = this._createAccountControls(cols.cols, existing);
    let transactionType = this.determineTransactionType(isCredit);

    return this.fb.group({
      notExported: this.fb.control(notExported),
      transactionType: this.fb.control(transactionType),
      accounts: accountControls,
      name: this.fb.control(row.Display)
    });
  }

  private _createAccountControls(
    cols: { key: string; name: string }[],
    existingRows: GlMappingRow[]
  ): FormArray {
    const controls = this.fb.array([]);
    cols.forEach(col => {
      //find out if mapping exists already
      let existing: GlMappingRow | null = null;
      existingRows.forEach(mapping => {
        //We have existing mappings for this row
        if (mapping.JobCode != null && mapping.JobCode.toString() == col.key) {
          existing = mapping;
        }
        if (mapping.DivisionNumber != null && mapping.DepartmentNumber != null) {
          //This is a Division and Department scenario
          const unitedCode = `${mapping.DivisionNumber}&${mapping.DepartmentNumber}`;
          if (col.key == unitedCode) {
            existing = mapping;
          }
        }
        if (
          mapping.DivisionNumber != null &&
          mapping.DepartmentNumber == null &&
          mapping.DivisionNumber.toString() == col.key
        ) {
          existing = mapping;
        }
        if (
          mapping.DivisionNumber == null &&
          mapping.DepartmentNumber != null &&
          mapping.DepartmentNumber.toString() == col.key
        ) {
          existing = mapping;
        }
        if (
          mapping.JobCode == null &&
          mapping.DivisionNumber == null &&
          mapping.DepartmentNumber == null
        ) {
          existing = mapping;
        }
      });
      const control: any = this.fb.group({
        key: this.fb.control(col.key),
        value: this.fb.control(existing?.GlAccount)
      });
      controls.push(control);
    });
    return controls;
  }

  private determineTransactionType(isCredit: boolean | null): string | null {
    if (isCredit === null) {
      return null;
    }

    return isCredit ? 'Credit' : 'Debit';
  }

  private _getColumnDefinitions(type: GLMappingLevelEnum): ColumnGroup {
    switch (type) {
      case GLMappingLevelEnum.COMPANY:
        return { cols: [{ key: 'Company', name: 'Company' }], topCols: [] };
      case GLMappingLevelEnum.DEPARTMENT:
        return {
          cols: this.departments.map(div => {
            return { key: div.Code, name: div.Display };
          }),
          topCols: []
        };
      case GLMappingLevelEnum.DIVISION:
        return {
          cols: this.divisions.map(div => {
            return { key: div.Code, name: div.Display };
          }),
          topCols: []
        };
      case GLMappingLevelEnum.JOB_CODE:
        return {
          cols: this.jobCodes.map(div => {
            return { key: div.Code, name: div.Display };
          }),
          topCols: []
        };
      case GLMappingLevelEnum.DIVISION_AND_DEPARTMENT:
        const cols = [];
        const topCols = [];
        this.divDept.forEach(div => {
          topCols.push({ key: div.Code, name: div.Display });
          div.DivisionChildren.forEach(dept => {
            cols.push({ key: dept.Code, name: dept.Display });
          });
        });
        return { cols, topCols };
      case GLMappingLevelEnum.NOT_EXPORTED:
        return { cols: [], topCols: [] };
    }
  }

  protected readonly CheckboxSizes = CheckboxSizes;
  protected readonly GlMappingTypes = GLMappingLevelEnum;
  protected readonly GeneralLedgerExportTypes = GeneralLedgerExportTypes;
}

export interface ColumnMap {
  key: string;
  name: string;
}
export interface ColumnGroup {
  cols: ColumnMap[];
  topCols: ColumnMap[];
}
